import type {
  WorkbookDefinition,
  WorkbookSheet,
  WorkbookTaskRule,
} from '@erp/shared-types';
import { resultColumns } from '@erp/workbooks';

/**
 * The columns a Task rule can title its Tasks by: its Worksheet's Result
 * columns, whatever its kind (a pivot's those known without data: its Rows
 * and grand total).
 */
export function taskTitleColumns(sheet: WorkbookSheet | undefined): string[] {
  return sheet ? resultColumns(sheet).map((column) => column.name) : [];
}

export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

/** A new Task rule on the first Worksheet, titled by its first column, unassigned. */
export function addTaskRule(
  definition: WorkbookDefinition,
  id: string,
): WorkbookDefinition {
  const [sheet] = definition.sheets;
  if (!sheet) return definition;
  return {
    ...definition,
    taskRules: [
      ...definition.taskRules,
      { id, sheetId: sheet.id, assigneeUserId: null, priority: 'medium' },
    ],
  };
}

/** One Task rule changed. Choosing another Worksheet drops a title column it doesn't have. */
export function updateTaskRule(
  definition: WorkbookDefinition,
  id: string,
  patch: Partial<Omit<WorkbookTaskRule, 'id'>>,
): WorkbookDefinition {
  return {
    ...definition,
    taskRules: definition.taskRules.map((rule) => {
      if (rule.id !== id) return rule;
      const next = { ...rule, ...patch };
      const sheet = definition.sheets.find((item) => item.id === next.sheetId);
      const keepsTitle =
        next.titleColumn !== undefined &&
        taskTitleColumns(sheet).includes(next.titleColumn);
      return keepsTitle ? next : { ...next, titleColumn: undefined };
    }),
  };
}

export function removeTaskRule(
  definition: WorkbookDefinition,
  id: string,
): WorkbookDefinition {
  return {
    ...definition,
    taskRules: definition.taskRules.filter((rule) => rule.id !== id),
  };
}
